import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import MainTemplate from "../../templates/MainTemplate";
import {
  Box,
  Typography,
  Paper,
  Grid,
  Button,
  InputBase,
  IconButton,
  Chip,
  Avatar,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Divider,
  CircularProgress,
  Tooltip,
  Snackbar,
} from "@mui/material";
import MuiAlert from "@mui/material/Alert";
import SearchIcon from "@mui/icons-material/Search";
import AddIcon from "@mui/icons-material/Add";
import DirectionsCarIcon from "@mui/icons-material/DirectionsCar";
import PhotoLibraryIcon from "@mui/icons-material/PhotoLibrary";
import CloudUploadIcon from "@mui/icons-material/CloudUpload";
import CloseIcon from "@mui/icons-material/Close";
import DescriptionIcon from "@mui/icons-material/Description";
import SaveIcon from "@mui/icons-material/Save";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";

import {
  getCarList,
  createCar,
  updateCar,
  deleteCar,
  updateCarImages,
} from "./service";
import { Bucket } from "../../../api/config/apiConfig";
import CarMediaUploaderViewer from "../../molecules/CarMediaUploaderViewer";
import moment from "moment";

const CarMediaPage = () => {
  const navigate = useNavigate();

  const [carList, setCarList] = useState([]);
  const [selectedCar, setSelectedCar] = useState(null);
  const [search, setSearch] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [deleteCarDialogId, setDeleteCarDialogId] = useState(null);

  // Add Car Modal
  const [isAddModalOpen, setIsAddModalOpen] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [newCarForm, setNewCarForm] = useState({
    make: "",
    model: "",
    year: moment().format("YYYY"),
    grade: "",
    specification: "",
    mileage: "",
    color: "",
    chassisNumber: "",
    clientName: "",
    clientEmail: "",
    contractPrice: "",
    images: [],
  });

  // Notification Snackbar
  const [openSnackbar, setOpenSnackbar] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState("");
  const [snackbarSeverity, setSnackbarSeverity] = useState("success");

  const notify = (msg, severity = "success") => {
    setSnackbarMessage(msg);
    setSnackbarSeverity(severity);
    setOpenSnackbar(true);
  };

  const fetchCars = async () => {
    setIsLoading(true);
    getCarList(1, 100, search)
      .then((res) => {
        const list = res?.data?.data?.carList || res?.data?.data || [];
        setCarList(list);
        if (list.length > 0) {
          setSelectedCar((prev) => {
            if (prev) {
              const match = list.find((item) => item.uuid === prev.uuid);
              return match || list[0];
            }
            return list[0];
          });
        } else {
          setSelectedCar(null);
        }
        setIsLoading(false);
      })
      .catch((err) => {
        console.error("Failed to fetch cars", err);
        setCarList([]);
        setSelectedCar(null);
        setIsLoading(false);
      });
  };

  useEffect(() => {
    fetchCars();
  }, [search]);

  // Handle image updates for selected car using dedicated car backend
  const handleCarImagesChange = (newImages) => {
    if (!selectedCar) return;

    const updated = {
      ...selectedCar,
      images: newImages,
    };

    setSelectedCar(updated);

    updateCarImages(selectedCar.uuid, newImages)
      .then(() => {
        notify("Car photos saved successfully to car database!", "success");
        setCarList((prev) =>
          prev.map((c) => (c.uuid === selectedCar.uuid ? updated : c))
        );
      })
      .catch((err) => {
        console.error("Failed to save car photos:", err);
        notify(err?.response?.data?.message || "Failed to save car photos", "error");
      });
  };

  // Submit new car & upload images using dedicated car backend POST /admin/car
  const handleCreateCarSubmit = () => {
    if (!newCarForm.make && !newCarForm.model) {
      notify("Please enter vehicle Make or Model", "error");
      return;
    }

    setIsSubmitting(true);

    const payload = {
      make: newCarForm.make,
      model: newCarForm.model,
      year: newCarForm.year,
      grade: newCarForm.grade,
      specification: newCarForm.specification,
      mileage: newCarForm.mileage,
      color: newCarForm.color,
      chassisNumber: newCarForm.chassisNumber,
      clientName: newCarForm.clientName,
      clientEmail: newCarForm.clientEmail,
      contractPrice: newCarForm.contractPrice,
      images: newCarForm.images || [],
      status: "Active",
    };

    createCar(payload)
      .then((res) => {
        notify("New car record & PNG/JPG media created successfully in database!", "success");
        setIsSubmitting(false);
        setIsAddModalOpen(false);
        setNewCarForm({
          make: "",
          model: "",
          year: moment().format("YYYY"),
          grade: "",
          specification: "",
          mileage: "",
          color: "",
          chassisNumber: "",
          clientName: "",
          clientEmail: "",
          contractPrice: "",
          images: [],
        });
        fetchCars();
      })
      .catch((err) => {
        console.error("Error adding car:", err);
        notify(err?.response?.data?.message || "Failed to create car record", "error");
        setIsSubmitting(false);
      });
  };

  const handleDeleteCar = (uuid) => {
    deleteCar(uuid)
      .then(() => {
        notify("Car record deleted successfully", "success");
        setDeleteCarDialogId(null);
        fetchCars();
      })
      .catch((err) => {
        notify(err?.response?.data?.message || "Failed to delete car", "error");
      });
  };

  const totalPhotos = carList.reduce(
    (acc, c) => acc + (c?.images?.length || 0),
    0
  );

  return (
    <MainTemplate>
      <Box sx={{ p: { xs: 2, md: 4 }, minHeight: "100vh", backgroundColor: "#f8fafc" }}>
        {/* Page Header */}
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            flexWrap: "wrap",
            gap: 2,
            mb: 4,
          }}
        >
          <Box>
            <Typography
              variant="h4"
              sx={{
                fontWeight: 800,
                color: "#0f172a",
                letterSpacing: "-0.5px",
                display: "flex",
                alignItems: "center",
                gap: 1.5,
              }}
            >
              <DirectionsCarIcon sx={{ fontSize: 38, color: "#0284c7" }} />
              Car Inventory & Media Gallery
            </Typography>
            <Typography variant="body1" sx={{ color: "#64748b", mt: 0.5 }}>
              Dedicated vehicle records and PNG/JPG photo management with independent backend data storage.
            </Typography>
          </Box>

          <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
            <Button
              variant="contained"
              startIcon={<CloudUploadIcon />}
              onClick={() => setIsAddModalOpen(true)}
              sx={{
                textTransform: "none",
                borderRadius: "12px",
                px: 3,
                py: 1.2,
                fontWeight: 700,
                fontSize: "0.95rem",
                background: "linear-gradient(135deg, #0284c7 0%, #0369a1 100%)",
                boxShadow: "0 4px 14px rgba(2, 132, 199, 0.3)",
                "&:hover": {
                  background: "linear-gradient(135deg, #0369a1 0%, #075985 100%)",
                },
              }}
            >
              + Add Car & Upload (PNG/JPG)
            </Button>
          </Box>
        </Box>

        {/* Stats Row */}
        <Grid container spacing={2.5} sx={{ mb: 4 }}>
          <Grid item xs={12} sm={6} md={3}>
            <Paper
              elevation={0}
              sx={{
                p: 2.5,
                borderRadius: "16px",
                border: "1px solid #e2e8f0",
                backgroundColor: "#ffffff",
                display: "flex",
                alignItems: "center",
                gap: 2,
              }}
            >
              <Box
                sx={{
                  width: 52,
                  height: 52,
                  borderRadius: "12px",
                  backgroundColor: "#e0f2fe",
                  color: "#0284c7",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                <DirectionsCarIcon sx={{ fontSize: 28 }} />
              </Box>
              <Box>
                <Typography variant="caption" sx={{ color: "#64748b", fontWeight: 600, textTransform: "uppercase" }}>
                  Registered Vehicles
                </Typography>
                <Typography variant="h5" sx={{ fontWeight: 800, color: "#0f172a" }}>
                  {carList.length} Cars
                </Typography>
              </Box>
            </Paper>
          </Grid>

          <Grid item xs={12} sm={6} md={3}>
            <Paper
              elevation={0}
              sx={{
                p: 2.5,
                borderRadius: "16px",
                border: "1px solid #e2e8f0",
                backgroundColor: "#ffffff",
                display: "flex",
                alignItems: "center",
                gap: 2,
              }}
            >
              <Box
                sx={{
                  width: 52,
                  height: 52,
                  borderRadius: "12px",
                  backgroundColor: "#fef3c7",
                  color: "#d97706",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                <PhotoLibraryIcon sx={{ fontSize: 28 }} />
              </Box>
              <Box>
                <Typography variant="caption" sx={{ color: "#64748b", fontWeight: 600, textTransform: "uppercase" }}>
                  Total Photos Uploaded
                </Typography>
                <Typography variant="h5" sx={{ fontWeight: 800, color: "#0f172a" }}>
                  {totalPhotos} Photos
                </Typography>
              </Box>
            </Paper>
          </Grid>

          <Grid item xs={12} sm={6} md={3}>
            <Paper
              elevation={0}
              sx={{
                p: 2.5,
                borderRadius: "16px",
                border: "1px solid #e2e8f0",
                backgroundColor: "#ffffff",
                display: "flex",
                alignItems: "center",
                gap: 2,
              }}
            >
              <Box
                sx={{
                  width: 52,
                  height: 52,
                  borderRadius: "12px",
                  backgroundColor: "#dcfce7",
                  color: "#16a34a",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                <CheckCircleIcon sx={{ fontSize: 28 }} />
              </Box>
              <Box>
                <Typography variant="caption" sx={{ color: "#64748b", fontWeight: 600, textTransform: "uppercase" }}>
                  Supported Formats
                </Typography>
                <Typography variant="h6" sx={{ fontWeight: 800, color: "#0f172a" }}>
                  PNG & JPG / JPEG
                </Typography>
              </Box>
            </Paper>
          </Grid>

          <Grid item xs={12} sm={6} md={3}>
            <Paper
              elevation={0}
              sx={{
                p: 2.5,
                borderRadius: "16px",
                border: "1px solid #e2e8f0",
                backgroundColor: "#ffffff",
                display: "flex",
                alignItems: "center",
                gap: 2,
              }}
            >
              <Box
                sx={{
                  width: 52,
                  height: 52,
                  borderRadius: "12px",
                  backgroundColor: "#f3e8ff",
                  color: "#9333ea",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                <DescriptionIcon sx={{ fontSize: 28 }} />
              </Box>
              <Box>
                <Typography variant="caption" sx={{ color: "#64748b", fontWeight: 600, textTransform: "uppercase" }}>
                  Backend Module
                </Typography>
                <Typography variant="h6" sx={{ fontWeight: 800, color: "#0f172a" }}>
                  Dedicated /admin/car
                </Typography>
              </Box>
            </Paper>
          </Grid>
        </Grid>

        {/* Main Content Layout: Left Vehicle Selector + Right Media Uploader/Viewer */}
        <Grid container spacing={3}>
          {/* Left Column: Vehicle Directory */}
          <Grid item xs={12} md={4} lg={3.5}>
            <Paper
              elevation={0}
              sx={{
                borderRadius: "16px",
                border: "1px solid #e2e8f0",
                backgroundColor: "#ffffff",
                p: 2.5,
                height: "100%",
                minHeight: "500px",
              }}
            >
              <Typography variant="h6" sx={{ fontWeight: 700, color: "#0f172a", mb: 2 }}>
                Vehicle Directory
              </Typography>

              {/* Search */}
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  backgroundColor: "#f8fafc",
                  border: "1px solid #e2e8f0",
                  borderRadius: "10px",
                  px: 1.5,
                  py: 0.5,
                  mb: 2.5,
                }}
              >
                <SearchIcon sx={{ color: "#94a3b8", mr: 1, fontSize: 20 }} />
                <InputBase
                  placeholder="Search vehicles..."
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  sx={{ width: "100%", fontSize: "0.85rem" }}
                />
              </Box>

              {/* Vehicles Scrollable List */}
              <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5, maxHeight: "650px", overflowY: "auto", pr: 0.5 }}>
                {isLoading ? (
                  <Box sx={{ py: 6, textAlign: "center" }}>
                    <CircularProgress size={28} sx={{ color: "#0284c7" }} />
                  </Box>
                ) : carList.length > 0 ? (
                  carList.map((car) => {
                    const isSelected = selectedCar?.uuid === car.uuid;
                    const imgCount = car?.images?.length || 0;
                    const coverImg = (car?.images || []).find(
                      (im) => im && typeof im === "object" && im.isCover
                    );
                    const firstImg = coverImg || car?.images?.[0];
                    const firstKey =
                      typeof firstImg === "string"
                        ? firstImg
                        : firstImg?.key || firstImg?.url;
                    const thumbUrl = firstKey
                      ? firstKey.startsWith("http")
                        ? firstKey
                        : `${Bucket}${firstKey}`
                      : null;

                    return (
                      <Paper
                        key={car.uuid}
                        elevation={0}
                        onClick={() => setSelectedCar(car)}
                        sx={{
                          p: 1.5,
                          borderRadius: "12px",
                          border: isSelected ? "2px solid #0284c7" : "1px solid #e2e8f0",
                          backgroundColor: isSelected ? "#f0f9ff" : "#ffffff",
                          cursor: "pointer",
                          transition: "all 0.2s ease",
                          "&:hover": {
                            borderColor: "#0284c7",
                            backgroundColor: "#f0f9ff",
                          },
                        }}
                      >
                        <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
                          <Avatar
                            src={thumbUrl}
                            variant="rounded"
                            sx={{
                              width: 48,
                              height: 48,
                              bgcolor: isSelected ? "#0284c7" : "#e0f2fe",
                              color: isSelected ? "#ffffff" : "#0284c7",
                              borderRadius: "10px",
                            }}
                          >
                            <DirectionsCarIcon />
                          </Avatar>
                          <Box sx={{ flex: 1, minWidth: 0 }}>
                            <Typography
                              variant="subtitle2"
                              sx={{
                                fontWeight: 700,
                                color: "#0f172a",
                                overflow: "hidden",
                                textOverflow: "ellipsis",
                                whiteSpace: "nowrap",
                              }}
                            >
                              {car?.make || "Vehicle"} {car?.model || ""}
                            </Typography>
                            <Typography variant="caption" sx={{ color: "#64748b", display: "block" }}>
                              {car?.year || "Year N/A"} • {car?.mileage || "Mileage N/A"}
                            </Typography>
                            <Box sx={{ display: "flex", alignItems: "center", gap: 1, mt: 0.5 }}>
                              <Chip
                                size="small"
                                icon={<PhotoLibraryIcon fontSize="small" />}
                                label={`${imgCount} Photos`}
                                sx={{
                                  height: 20,
                                  fontSize: "0.7rem",
                                  fontWeight: 600,
                                  bgcolor: isSelected ? "#0284c7" : "#f1f5f9",
                                  color: isSelected ? "#ffffff" : "#475569",
                                  "& .MuiChip-icon": {
                                    color: isSelected ? "#ffffff" : "#64748b",
                                  },
                                }}
                              />
                            </Box>
                          </Box>
                        </Box>
                      </Paper>
                    );
                  })
                ) : (
                  <Box sx={{ py: 6, textAlign: "center" }}>
                    <DirectionsCarIcon sx={{ fontSize: 40, color: "#cbd5e1", mb: 1 }} />
                    <Typography variant="body2" sx={{ color: "#64748b" }}>
                      No cars registered in car database yet.
                    </Typography>
                    <Button
                      size="small"
                      startIcon={<AddIcon />}
                      onClick={() => setIsAddModalOpen(true)}
                      sx={{ mt: 1.5, textTransform: "none", fontWeight: 700 }}
                    >
                      Add First Car
                    </Button>
                  </Box>
                )}
              </Box>
            </Paper>
          </Grid>

          {/* Right Column: Selected Car Media Uploader & Interactive Lightbox Viewer */}
          <Grid item xs={12} md={8} lg={8.5}>
            {selectedCar ? (
              <Paper
                elevation={0}
                sx={{
                  borderRadius: "16px",
                  border: "1px solid #e2e8f0",
                  backgroundColor: "#ffffff",
                  p: { xs: 2.5, md: 4 },
                }}
              >
                {/* Active Vehicle Info Banner */}
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    flexWrap: "wrap",
                    gap: 2,
                    p: 2.5,
                    borderRadius: "14px",
                    backgroundColor: "#f8fafc",
                    border: "1px solid #e2e8f0",
                    mb: 4,
                  }}
                >
                  <Box>
                    <Typography variant="h5" sx={{ fontWeight: 800, color: "#0f172a" }}>
                      {selectedCar?.make || "Vehicle"} {selectedCar?.model || ""}
                    </Typography>
                    <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap", mt: 1 }}>
                      <Chip
                        label={`Year: ${selectedCar?.year || "N/A"}`}
                        size="small"
                        sx={{ fontWeight: 600, bgcolor: "#ffffff" }}
                      />
                      <Chip
                        label={`Grade: ${selectedCar?.grade || "N/A"}`}
                        size="small"
                        sx={{ fontWeight: 600, bgcolor: "#ffffff" }}
                      />
                      <Chip
                        label={`Mileage: ${selectedCar?.mileage || "N/A"}`}
                        size="small"
                        sx={{ fontWeight: 600, bgcolor: "#ffffff" }}
                      />
                      {selectedCar?.chassisNumber && (
                        <Chip
                          label={`Chassis/VIN: ${selectedCar?.chassisNumber}`}
                          size="small"
                          sx={{ fontWeight: 600, bgcolor: "#ffffff" }}
                        />
                      )}
                      <Chip
                        label={`Specs: ${selectedCar?.specification || "Standard"}`}
                        size="small"
                        sx={{ fontWeight: 600, bgcolor: "#ffffff" }}
                      />
                    </Box>
                  </Box>

                  <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                    <Tooltip title="Delete Car Record">
                      <IconButton
                        size="small"
                        onClick={() => setDeleteCarDialogId(selectedCar.uuid)}
                        sx={{ color: "#dc2626" }}
                      >
                        <DeleteOutlineIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>

                    {/* Create Prebid with this Car */}
                    <Button
                      variant="outlined"
                      endIcon={<ArrowForwardIcon />}
                      onClick={() => navigate("/create-update-prebid/uuid")}
                      sx={{
                        textTransform: "none",
                        borderRadius: "10px",
                        fontWeight: 700,
                        color: "#0284c7",
                        borderColor: "#0284c7",
                        "&:hover": {
                          bgcolor: "#f0f9ff",
                          borderColor: "#0369a1",
                        },
                      }}
                    >
                      Create Prebid Agreement
                    </Button>
                  </Box>
                </Box>

                {/* Car Media Uploader & Viewer */}
                <CarMediaUploaderViewer
                  images={selectedCar?.images || []}
                  onImagesChange={handleCarImagesChange}
                  isEdit={true}
                  onNotify={notify}
                  title={`Car Photos & Documentation`}
                  subtitle="Upload PNG and JPG exterior, interior, engine bay photos, and auction sheets."
                />
              </Paper>
            ) : (
              <Paper
                elevation={0}
                sx={{
                  borderRadius: "16px",
                  border: "1px solid #e2e8f0",
                  backgroundColor: "#ffffff",
                  p: 6,
                  textAlign: "center",
                }}
              >
                <DirectionsCarIcon sx={{ fontSize: 56, color: "#cbd5e1", mb: 2 }} />
                <Typography variant="h6" sx={{ fontWeight: 700, color: "#334155" }}>
                  Select or Add a Vehicle
                </Typography>
                <Typography variant="body2" sx={{ color: "#64748b", mt: 0.5, mb: 3 }}>
                  Select a car from the directory on the left or click below to add a new car and upload its PNG/JPG photos.
                </Typography>
                <Button
                  variant="contained"
                  startIcon={<CloudUploadIcon />}
                  onClick={() => setIsAddModalOpen(true)}
                  sx={{
                    textTransform: "none",
                    borderRadius: "10px",
                    fontWeight: 700,
                    px: 3,
                    bgcolor: "#0284c7",
                  }}
                >
                  Add Car & Upload Photos
                </Button>
              </Paper>
            )}
          </Grid>
        </Grid>

        {/* ADD CAR & UPLOAD MEDIA MODAL */}
        <Dialog
          open={isAddModalOpen}
          onClose={() => !isSubmitting && setIsAddModalOpen(false)}
          maxWidth="lg"
          fullWidth
          PaperProps={{
            sx: {
              borderRadius: "18px",
              p: 1,
              maxHeight: "92vh",
              overflow: "hidden",
            },
          }}
        >
          <DialogTitle sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", pb: 1 }}>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <DirectionsCarIcon sx={{ color: "#0284c7", fontSize: 28 }} />
              <Box>
                <Typography variant="h6" sx={{ fontWeight: 800, color: "#0f172a" }}>
                  Add Car & Upload Media (PNG / JPG)
                </Typography>
                <Typography variant="caption" sx={{ color: "#64748b" }}>
                  Save directly to the dedicated car backend repository.
                </Typography>
              </Box>
            </Box>
            <IconButton onClick={() => setIsAddModalOpen(false)} disabled={isSubmitting}>
              <CloseIcon />
            </IconButton>
          </DialogTitle>

          <Divider />

          <DialogContent sx={{ py: 3 }}>
            <Typography variant="subtitle2" sx={{ fontWeight: 700, color: "#1e293b", mb: 2 }}>
              1. Vehicle Specifications
            </Typography>

            <Grid container spacing={2} sx={{ mb: 3 }}>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  size="small"
                  label="Make / Brand *"
                  placeholder="e.g. Toyota, Honda, Mercedes-Benz"
                  value={newCarForm.make}
                  onChange={(e) => setNewCarForm({ ...newCarForm, make: e.target.value })}
                />
              </Grid>

              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  size="small"
                  label="Model & Variant *"
                  placeholder="e.g. Land Cruiser Prado TX, Civic"
                  value={newCarForm.model}
                  onChange={(e) => setNewCarForm({ ...newCarForm, model: e.target.value })}
                />
              </Grid>

              <Grid item xs={12} sm={4}>
                <TextField
                  fullWidth
                  size="small"
                  label="Manufacturing Year"
                  placeholder="e.g. 2023"
                  value={newCarForm.year}
                  onChange={(e) => setNewCarForm({ ...newCarForm, year: e.target.value })}
                />
              </Grid>

              <Grid item xs={12} sm={4}>
                <TextField
                  fullWidth
                  size="small"
                  label="Grade / Package"
                  placeholder="e.g. Package L, 4.5 Grade"
                  value={newCarForm.grade}
                  onChange={(e) => setNewCarForm({ ...newCarForm, grade: e.target.value })}
                />
              </Grid>

              <Grid item xs={12} sm={4}>
                <TextField
                  fullWidth
                  size="small"
                  label="Mileage"
                  placeholder="e.g. 25,000 km"
                  value={newCarForm.mileage}
                  onChange={(e) => setNewCarForm({ ...newCarForm, mileage: e.target.value })}
                />
              </Grid>

              <Grid item xs={12} sm={4}>
                <TextField
                  fullWidth
                  size="small"
                  label="Chassis / VIN Number"
                  placeholder="e.g. GDJ150-001234"
                  value={newCarForm.chassisNumber}
                  onChange={(e) => setNewCarForm({ ...newCarForm, chassisNumber: e.target.value })}
                />
              </Grid>

              <Grid item xs={12} sm={4}>
                <TextField
                  fullWidth
                  size="small"
                  label="Exterior Color"
                  placeholder="e.g. Pearl White, Black"
                  value={newCarForm.color}
                  onChange={(e) => setNewCarForm({ ...newCarForm, color: e.target.value })}
                />
              </Grid>

              <Grid item xs={12} sm={4}>
                <TextField
                  fullWidth
                  size="small"
                  label="Specification / Features"
                  placeholder="e.g. 4WD, Sunroof, Leather"
                  value={newCarForm.specification}
                  onChange={(e) => setNewCarForm({ ...newCarForm, specification: e.target.value })}
                />
              </Grid>

              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  size="small"
                  label="Client / Owner Name"
                  placeholder="e.g. John Doe"
                  value={newCarForm.clientName}
                  onChange={(e) => setNewCarForm({ ...newCarForm, clientName: e.target.value })}
                />
              </Grid>

              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  size="small"
                  label="Client Email"
                  placeholder="e.g. client@example.com"
                  value={newCarForm.clientEmail}
                  onChange={(e) => setNewCarForm({ ...newCarForm, clientEmail: e.target.value })}
                />
              </Grid>
            </Grid>

            <Divider sx={{ my: 2.5 }} />

            <Typography variant="subtitle2" sx={{ fontWeight: 700, color: "#1e293b", mb: 1 }}>
              2. Upload Car Photos & Inspection Sheets (PNG / JPG)
            </Typography>

            <CarMediaUploaderViewer
              images={newCarForm.images || []}
              onImagesChange={(imgs) => setNewCarForm({ ...newCarForm, images: imgs })}
              isEdit={true}
              onNotify={notify}
              title="Car Photos Dropzone"
              subtitle="Drag & drop PNG or JPG images of this vehicle, auction sheets, or interior/exterior views."
            />
          </DialogContent>

          <Divider />

          <DialogActions sx={{ px: 3, py: 2 }}>
            <Button
              onClick={() => setIsAddModalOpen(false)}
              disabled={isSubmitting}
              sx={{ textTransform: "none", fontWeight: 600, color: "#64748b" }}
            >
              Cancel
            </Button>
            <Button
              variant="contained"
              startIcon={isSubmitting ? <CircularProgress size={18} sx={{ color: "#fff" }} /> : <SaveIcon />}
              disabled={isSubmitting}
              onClick={handleCreateCarSubmit}
              sx={{
                textTransform: "none",
                fontWeight: 700,
                borderRadius: "10px",
                px: 3,
                background: "linear-gradient(135deg, #0284c7 0%, #0369a1 100%)",
              }}
            >
              {isSubmitting ? "Saving Car..." : "Save Car & Media"}
            </Button>
          </DialogActions>
        </Dialog>

        {/* Delete Car Confirmation Modal */}
        <Dialog
          open={Boolean(deleteCarDialogId)}
          onClose={() => setDeleteCarDialogId(null)}
          PaperProps={{ sx: { borderRadius: "16px", p: 1 } }}
        >
          <DialogTitle sx={{ fontWeight: 700, color: "#0f172a" }}>
            Delete Vehicle Record?
          </DialogTitle>
          <DialogContent>
            <Typography variant="body2" sx={{ color: "#64748b" }}>
              Are you sure you want to delete this car record and its media? This action cannot be undone.
            </Typography>
          </DialogContent>
          <DialogActions sx={{ px: 3, pb: 2 }}>
            <Button
              onClick={() => setDeleteCarDialogId(null)}
              sx={{ textTransform: "none", fontWeight: 600, color: "#64748b" }}
            >
              Cancel
            </Button>
            <Button
              variant="contained"
              color="error"
              onClick={() => handleDeleteCar(deleteCarDialogId)}
              sx={{ textTransform: "none", fontWeight: 700, borderRadius: "10px" }}
            >
              Delete Car
            </Button>
          </DialogActions>
        </Dialog>

        {/* Global Notifications Snackbar */}
        <Snackbar
          open={openSnackbar}
          autoHideDuration={5000}
          onClose={() => setOpenSnackbar(false)}
          anchorOrigin={{ vertical: "top", horizontal: "right" }}
        >
          <MuiAlert
            elevation={6}
            variant="filled"
            severity={snackbarSeverity}
            onClose={() => setOpenSnackbar(false)}
          >
            {snackbarMessage}
          </MuiAlert>
        </Snackbar>
      </Box>
    </MainTemplate>
  );
};

export default CarMediaPage;
